import { PermissionEnum } from '@creditsights/api-ui-web';
import { Checkbox, Dropdown, Loader, ToasterMode } from '@creditsights/styleguide-web';
import { debounce, kebabCase } from 'lodash';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { toasterState } from '../../../atoms/toaster.atom';
import { USER_EMAIL_PREFERENCES_CACHE_KEY } from '../../../Constants';
import { emailSettings } from '../../../data/email-preferences';
import useFetchUserEmailPreferences from '../../../hooks/user/useFetchUserEmailPreferences';
import useFetchUserPermissions from '../../../hooks/user/useFetchUserPermissions';
import useSaveUserEmailPreferences from '../../../hooks/user/useSaveUserEmailPreferences';
import UserAccountHeader from '../UserAccountHeader';
import './EmailPreferences.scss';

function EmailPreferences(): ReactElement {
  const queryClient = useQueryClient();
  const { data: preferences, isLoading } = useFetchUserEmailPreferences();
  const { data: userPermissions, isLoading: isPermissionsLoading } = useFetchUserPermissions();
  const { mutate: saveEmailPreferences, isLoading: isSaving } = useSaveUserEmailPreferences();

  const researchAccess = useMemo(() => userPermissions?.permissions.includes(PermissionEnum.RESEARCH), [userPermissions]);

  const [tags, setSelectedTags] = useState<string[]>([]);
  const [pauseAllNotifications, setPauseAllNotifications] = useState(false);
  const [editorsPicks, setEditorsPicks] = useState(false);
  const [alertTimeZone, setAlertTimeZone] = useState<any>();
  const [activeView, setActiveView] = useState<any>();
  const showToaster = useSetRecoilState(toasterState);

  useEffect(() => {
    setSelectedTags(preferences?.tags ?? []);
    setEditorsPicks(preferences?.editorsPicks ?? false);
    setPauseAllNotifications(preferences?.pauseAllNotifications ?? false);
    setAlertTimeZone(preferences?.alertTimeZone);
  }, [preferences]);

  const isTagSelected = (tagId) => tags.indexOf(tagId) !== -1;

  const save = (newValues) => {
    saveEmailPreferences(newValues, {
      onSuccess: () => {
        queryClient.invalidateQueries([USER_EMAIL_PREFERENCES_CACHE_KEY]);
        showToaster({
          text: 'Email Preferences saved.',
        });
      },
      onError: () => {
        showToaster({
          text: 'Saving Email Preferences failed.',
          mode: ToasterMode.ERROR,
        });
      },
    });
  };

  const debouncedSave = useMemo(() => debounce(save, 1000), []);

  const onTagToggle = (tagId, checked) => {
    const newValue = checked ? tags.filter((t) => t !== tagId) : [...tags, tagId];
    setSelectedTags(newValue);

    debouncedSave({
      alertTimeZone,
      editorsPicks,
      pauseAllNotifications,
      tags: newValue,
    });
  };

  const onPauseAllNotificationsToggle = () => {
    const newValue = !pauseAllNotifications;
    setPauseAllNotifications(newValue);

    debouncedSave({
      alertTimeZone,
      editorsPicks,
      pauseAllNotifications: newValue,
      tags,
    });
  };

  const onEditorPicksToggle = () => {
    const newValue = !editorsPicks;
    setEditorsPicks(newValue);

    debouncedSave({
      alertTimeZone,
      editorsPicks: newValue,
      pauseAllNotifications,
      tags,
    });
  };

  const onTimeZoneSelected = (newValue) => {
    setAlertTimeZone(newValue);

    debouncedSave({
      alertTimeZone: newValue,
      editorsPicks,
      pauseAllNotifications,
      tags,
    });
  };

  const renderSources = ({ research = false, news = false, covenants = false }) => {
    return (
      <>
        {research && <div className="source research">Research</div>}
        {news && <div className="source news">News</div>}
        {covenants && <div className="source covenants">Covenants</div>}
      </>
    );
  };

  const renderTagPills = (items) => {
    return (
      <div className="pills-ctx">
        {items.map((item) => {
          const isSelected = isTagSelected(item.tagId);

          return (
            <button type="button" className={`pill ${isSelected ? 'active' : 'inactive'}`} onClick={() => onTagToggle(item.tagId, isSelected)} disabled={isSaving} key={item.tagId}>
              <i className="check icon" />
              <div className="text">{item.name}</div>
            </button>
          );
        })}
      </div>
    );
  };

  const renderToggle = (tagId) => {
    const checked = isTagSelected(tagId);
    const label = checked ? 'On' : 'Off';

    return <Checkbox toggle dataTestId={tagId} checked={checked} label={label} onChange={() => onTagToggle(tagId, checked)} disabled={isSaving} />;
  };

  const renderNavItem = (label: string, sectionTitle: string) => {
    const elementId = kebabCase(sectionTitle);
    return (
      <div className={`nav-item ${activeView === sectionTitle ? 'active' : ''}`} onClick={() => scrollToView(elementId)}>
        {label}
      </div>
    );
  };

  const renderTagToggles = (items) => {
    return items.map((item) => (
      <div className="sub-section" key={item.tagId}>
        <div className="sub-title">{item.name}</div>
        <div className="description">{item.description}</div>
        {renderToggle(item.tagId)}
      </div>
    ));
  };

  const renderTimezoneDropdown = (timezones) => {
    return (
      <div className="dropdown-ctx">
        <div className="label">Receive email at 6:00am</div>
        <Dropdown value={alertTimeZone} disabled={isSaving} placeholder="Select timezone" options={timezones} onOptionSelect={(e) => onTimeZoneSelected(e)} />
      </div>
    );
  };

  const renderSection = (sectionConfig, mode: 'pills' | 'checkboxes') => {
    const { title, options, description, sources, timezones } = sectionConfig;

    return (
      <div className="section" id={kebabCase(title)}>
        <div className="title">
          <div className="text">{title}</div>
          {renderSources(sources)}
        </div>
        {description && <div className="description">{description}</div>}
        {timezones && renderTimezoneDropdown(timezones)}
        {mode === 'checkboxes' && renderTagToggles(options)}
        {mode === 'pills' && renderTagPills(options)}
      </div>
    );
  };

  const scrollToView = (viewId: string) => {
    const target = document.getElementById(viewId);
    if (target) {
      const parent = target.parentElement;
      if (parent) {
        parent.scrollTop = 0;
        const box = target.getBoundingClientRect();
        const parentBox = parent.getBoundingClientRect();
        parent.scrollTop = box.top - parentBox.top;
      }
    }

    setActiveView(viewId);
  };

  if (!isLoading && !isPermissionsLoading) {
    return (
      <div className="cs-user-email-preferences">
        <UserAccountHeader />
        <div className="preferences-ctx">
          <div className="navigation">
            <div className="header">All</div>
            {renderNavItem('Digests', 'digests')}
            {researchAccess && (
              <>
                <div className="header">Research</div>
                {renderNavItem('Morning Comment', 'morning-comment')}
                {renderNavItem('Weekly Reader', 'weekly-reader')}
                {renderNavItem('Summaries', 'summaries')}
              </>
            )}
          </div>
          <div className="contents">
            {renderSection(emailSettings.digests, 'checkboxes')}
            {researchAccess && renderSection(emailSettings.morningComment, 'pills')}
            {researchAccess && renderSection(emailSettings.weeklyReader, 'pills')}
            {researchAccess && renderSection(emailSettings.summaries, 'checkboxes')}
          </div>
          <div className="options-ctx">
            <div className="options">
              <div className="all section">
                <Checkbox toggle dataTestId="pause-all-notifications" checked={pauseAllNotifications} label="Pause all notifications" onChange={() => onPauseAllNotificationsToggle()} disabled={isSaving} />
              </div>
              <div className="editor-picks section">
                <div className="title">Editor’s Picks</div>
                <div className="description">
                  Editor’s Picks are featured articles selected by the CreditSights and LevFin Insights teams. Receive instant alerts on key research published within your interests instead of waiting for it in your daily summary.
                </div>
                <Checkbox toggle dataTestId="pause-all-notifications" checked={editorsPicks} label={editorsPicks ? 'On' : 'Off'} onChange={() => onEditorPicksToggle()} disabled={isSaving} />
              </div>
              <div className="links section">
                <Link to="/my-content/watchlists">Go to Watchlist settings</Link>
                <Link to="/my-content/preferences">Go to My Content preferences</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return <Loader className="cs-loader" position="center" />;
}

export default EmailPreferences;
